import React, { useState, useMemo, ChangeEvent } from 'react';

import ciPageEndedEvent from '../../../../assets/ci-page-ended-event.png';
import eventEditError from '../../../../assets/event-edit-error.png';
import eventUgcReq from '../../../../assets/event-ugc-req.png';

import { FloatingLabel } from '../../../../components/form';

import {
  EVENT_TYPE_VIDEOGRAPHER,
  EVENT_TYPE_BRAND,
  REC_WILDCARDS,
} from '../../../../constants';

import { Event, ProjectType } from '../../../../types';
import { isEmpty } from '../../../../utils';

import InfoImg from '../components/infoImg';
import TabComponent from '../components/tabComponent';
import ProjectDatesSelector from './projectDatesSelector';
import ProjectTypeSelector from './projectTypeSelector';
import ProjectVideoModeSelector from './projectVideoModeSelector';
import ProjectWildcardsSelector from './projectWildcardsSelector';

interface Props {
  event: Event;
}

const GeneralTab: React.FC<Props> = (props) => {
  const { event } = props;

  const [data, setData] = useState<Partial<Event>>({
    name: event.name ?? 'Untitled',
    type: event.type ?? EVENT_TYPE_VIDEOGRAPHER,
    startDate: event.startDate?.slice(0, 10) ?? '',
    endDate: event.endDate?.slice(0, 10) ?? '',
    videoMode: event.videoMode ?? undefined,
    wildcards: event.wildcards ?? REC_WILDCARDS,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e?: ChangeEvent<HTMLInputElement>,
    newName?: string,
    newValue?: any,
  ) => {
    const { name, value } = e?.target ?? {
      name: newName ?? '',
      value: newValue ?? '',
    };

    setData((prevData) => ({ ...prevData, [name]: value }));

    setErrors({});
  };

  const changeType = (value: ProjectType) => {
    setData((prevData) => ({ ...prevData, type: value }));

    if (value === EVENT_TYPE_BRAND && isEmpty(data.wildcards))
      setData((prevData) => ({ ...prevData, wildcards: REC_WILDCARDS }));

    setErrors({});
  };

  const videoReqInfoBody = (
    <React.Fragment>
      <div>
        Based on the selected orientation and wildcards attendees will be
        required to upload the corresponding UGC on the Check-In page.
      </div>

      <InfoImg src={eventUgcReq} alt="UGC Requirements Graphic" />

      <div className="font-bold">
        The uploaded template should also match these requisites.
      </div>
    </React.Fragment>
  );

  const isBrand = useMemo(() => data.type === EVENT_TYPE_BRAND, [data.type]);

  return (
    <TabComponent
      event={event}
      data={isBrand ? data : { name: data.name, type: data.type }}
      validationType={`general-${data.type}`}
      setErrors={setErrors}
      blocks={[
        {
          title: 'Project Name',
          description: 'Used to identify your project',
          content: (
            <FloatingLabel
              label="Project name"
              name="name"
              value={data.name ?? ''}
              onChange={handleChange}
              isInvalid={!!errors.name}
              errMsg={errors.name}
              required
            />
          ),
        },
        {
          // temporary removed
          isHide: true,
          title: 'Project Type',
          content: (
            <ProjectTypeSelector
              type={data.type}
              changeType={changeType}
              error={errors.type}
            />
          ),
        },
        {
          isHide: !isBrand,
          title: 'Start & End Date',
          description: 'Start and end dates determine the project availability',
          infoHeader: 'Setting up your video requisites',
          infoBody: (
            <React.Fragment>
              <div>You will see next on this page after the start date</div>

              <InfoImg src={eventEditError} alt="Event Edit Error Graphic" />

              <div>
                Attendees will see next on Check-In page after the end date
              </div>

              <InfoImg
                src={ciPageEndedEvent}
                alt="Check–In Page Ended Event Graphic"
              />
            </React.Fragment>
          ),
          content: (
            <ProjectDatesSelector
              data={data}
              handleChange={handleChange}
              errors={errors}
            />
          ),
        },
        {
          isHide: !isBrand,
          title: 'Orientation',
          description:
            'Template and user UGC will be converted to the selected orientation.',
          infoHeader: 'Setting up your video requisites',
          infoBody: videoReqInfoBody,
          content: (
            <ProjectVideoModeSelector
              videoMode={data.videoMode}
              onChangeVideoMode={(mode) =>
                handleChange(undefined, 'videoMode', mode)
              }
              error={errors.videoMode}
            />
          ),
        },
        {
          isHide: !isBrand,
          title: 'Wildcards',
          description:
            'This will determine what UGC attendees are asked to upload. Note that your template should also match with this selection.',
          infoHeader: 'Setting up your video requisites',
          infoBody: videoReqInfoBody,
          content: (
            <ProjectWildcardsSelector
              wildcards={data.wildcards ?? []}
              onChangeWildcards={(newWildcards) =>
                handleChange(undefined, 'wildcards', newWildcards)
              }
              error={errors.wildcards}
            />
          ),
        },
      ]}
    />
  );
};

export default GeneralTab;
